import { useCallback, useEffect, useRef, useState } from "react";
import { useFullLoader } from "./store";

// 'none' (<300 ms) · 'inline' (300 ms–1,5 s) · 'full' (>1,5 s o heavy). Mínimo 500 ms visible.
export function useLoadingLevel(isLoading, { heavy = false } = {}) {
  const [level, setLevel] = useState("none");
  const shownAt = useRef(0);
  const levelRef = useRef(level);
  levelRef.current = level;
  useEffect(() => {
    if (isLoading) {
      const show = (lv) => { if (levelRef.current === "none") shownAt.current = Date.now(); setLevel(lv); };
      const t1 = setTimeout(() => show(heavy ? "full" : "inline"), 300);
      const t2 = setTimeout(() => show("full"), 1500);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    if (levelRef.current === "none") return undefined;
    const t = setTimeout(() => setLevel("none"), Math.max(0, 500 - (Date.now() - shownAt.current)));
    return () => clearTimeout(t);
  }, [isLoading, heavy]);
  return level;
}

// Acción asíncrona de un botón: sin doble toque, ✓ 600 ms al terminar.
// `fn` puede devolver `false` (falló, sin ✓) o una función a ejecutar tras el ✓.
export function useAsyncAction(fn) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const busy = useRef(false);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const run = useCallback(async (...args) => {
    if (busy.current) return undefined;
    busy.current = true;
    setLoading(true);
    setDone(false);
    let res;
    try {
      res = await fnRef.current(...args);
    } catch (e) {
      setLoading(false);
      busy.current = false;
      throw e;
    }
    setLoading(false);
    if (res === false) { busy.current = false; return res; }
    setDone(true);
    await new Promise((r) => setTimeout(r, 600));
    setDone(false);
    busy.current = false;
    if (typeof res === "function") res();
    return res;
  }, []);
  return { run, loading, done };
}

// Proceso pesado (Nivel A): devuelve `set({step, progress})` para marcar hitos reales.
export function useHeavyFlow(loading, steps) {
  const [st, set] = useState({ step: 0, progress: null });
  useEffect(() => { if (loading) set({ step: 0, progress: null }); }, [loading]);
  const level = useLoadingLevel(loading, { heavy: true });
  useFullLoader(level === "full", { steps, ...st });
  return set;
}

export function useSlow(active, ms = 12000) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    setSlow(false);
    if (!active) return undefined;
    const t = setTimeout(() => setSlow(true), ms);
    return () => clearTimeout(t);
  }, [active, ms]);
  return slow;
}
